import React, { useCallback, useReducer } from 'react';
import { createVideoCategoryReducer } from './createVideoCategoryReducer';
import {
	CreateVideoCategoryContext,
	createVideoCategoryInitialState,
	TCreateVideoCategoryVisibleForm,
	TTypeOfCreateVideoCategoryModalWindow,
} from './createVideoCategoryContext';
import { CreateVideoCategoryActionTypes } from './createVideoCategoryActions';
import { IItemForGetShortInfo, TLanguageIDs, ICreateVideoCategory, IVideoCategoryTranslation } from 'types';

const CreateVideoCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createVideoCategoryReducer, createVideoCategoryInitialState);

	const changeVisibleForm = useCallback((value: TCreateVideoCategoryVisibleForm) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const changeName = useCallback((value: string) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.CHANGE_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedLanguages = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.SET_FETCHED_LANGUAGES,
			payload: {
				languages: value,
			},
		});
	}, []);

	const setIDsLanguages = useCallback(
		(value: TLanguageIDs[]) => {
			dispatch({
				type: CreateVideoCategoryActionTypes.SET_IDs_LANGUAGES,
				payload: {
					IDsLanguages: value,
				},
			});

			const isNewLanguageAdded = value.length > state.translations.length;
			if (isNewLanguageAdded) {
				const existingTranslationIDs = state.translations.map((el) => el.languageId);
				const newTranslationID = value.find((el) => !existingTranslationIDs.includes(el));
				if (newTranslationID) {
					const newTranslation: IVideoCategoryTranslation = {
						languageId: newTranslationID,
						name: '',
					};
					const updatedTranslations = [...state.translations, newTranslation];
					dispatch({
						type: CreateVideoCategoryActionTypes.SET_TRANSLATION_FIELDS,
						payload: {
							updatedTranslations,
						},
					});
				}
			} else {
				const updatedTranslations = state.translations.filter((el) => value.includes(el.languageId));
				dispatch({
					type: CreateVideoCategoryActionTypes.SET_TRANSLATION_FIELDS,
					payload: {
						updatedTranslations,
					},
				});
			}
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateVideoCategory | null) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				data: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfCreateVideoCategoryModalWindow) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateVideoCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				translationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateVideoCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const resetFields = useCallback(() => dispatch({ type: CreateVideoCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<CreateVideoCategoryContext.Provider
			value={{
				changeVisibleForm,
				changeName,
				setFetchedLanguages,
				setIDsLanguages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				setTranslationTab,
				changeNameTranslation,
				resetFields,
				visibleForm: state.visibleForm,
				name: state.name,
				fetchedLanguages: state.fetchedLanguages,
				IDsSelectedLanguages: state.IDsSelectedLanguages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
			}}>
			{children}
		</CreateVideoCategoryContext.Provider>
	);
};

export default CreateVideoCategoryState;
